import { createContext, useContext, useEffect } from "react";
import { useTabs } from "./TabContext";

// value shape: { focusKey: number, tabId: string }
// focusKey bumps each time the containing tab becomes active (kept for
// existing consumers that just want a remount/refresh signal).
// tabId identifies which tab this component tree is rendered inside, so a
// form can report its own dirty state without prop-drilling.
export const TabRefreshContext = createContext({ focusKey: 0, tabId: null });

export function useTabRefresh() {
  const ctx = useContext(TabRefreshContext);
  // Back-compat: existing call sites do `const focusKey = useTabRefresh()`.
  // Returning the number directly preserves that, while ctx.tabId remains
  // reachable via useTabRefreshContext() for anything that needs it.
  return ctx.focusKey;
}

export function useTabRefreshContext() {
  return useContext(TabRefreshContext);
}

/**
 * Registers this tab as dirty/clean based on `isDirty`. Call from any
 * form/screen that holds unsaved work — the tab bar will then block
 * refresh/close/browser-close until it's saved or the user confirms.
 *
 * Usage:
 *   const [isDirty, setIsDirty] = useState(false);
 *   useTabDirty(isDirty);
 *   // ...set isDirty(true) on change, isDirty(false) after a successful save
 */
export function useTabDirty(isDirty) {
  const { tabId } = useTabRefreshContext();
  const tabsCtx = useTabs();

  useEffect(() => {
    if (!tabId || !tabsCtx?.setTabDirty) return;
    tabsCtx.setTabDirty(tabId, !!isDirty);
  }, [tabId, isDirty, tabsCtx]);

  // Clear dirty flag on unmount (e.g. user navigated away within the tab
  // after saving, or the component was removed) so we don't leave a stale
  // dirty flag blocking future refreshes of that tab.
  useEffect(() => {
    return () => {
      if (tabId && tabsCtx?.setTabDirty) {
        tabsCtx.setTabDirty(tabId, false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabId]);
}
